import { FC } from "react";
import { sanitize } from "isomorphic-dompurify";
import { Trans } from "@lingui/macro";
import {
  StyledBrandInfo,
  StyledBrandInfoContent,
  StyledCloseButton,
  StyledConsciousContent,
  StyledInfoHeader,
} from "./BrandInfo.styled";
import { GoodOnYou } from "src/molecules/GoodOnYou";
import { Text, Heading } from "@otrium/atoms";
import { Box, useBreakpoints } from "@otrium/core";
import { AlgoliaSearchBrand } from "src/types/Algolia";

interface Props {
  brandInfo: AlgoliaSearchBrand;
  onClose: () => void;
  pageType: "pdp" | "brandsPage";
}

const BrandInfo: FC<Props> = ({ brandInfo, onClose, pageType, ...rest }) => {
  const brandName = brandInfo.name;
  const ratingInfo = `${
    brandInfo.good_on_you_info?.score_meta.split("||")[0]
  }/5 (${brandInfo.good_on_you_info?.score_meta.split("||")[1]})`;
  const { isMobile } = useBreakpoints();

  return (
    <StyledBrandInfo {...rest} isMobile={isMobile}>
      <StyledInfoHeader>
        <Heading
          as="h2"
          fontSize={3}
          fontFamily="PlusJakartaSans"
          color="tone.black"
          letterSpacing="initial"
          textAlign="center"
          sx={{ textTransform: "unset" }}
        >
          <Trans>
            About&nbsp;
            <span dangerouslySetInnerHTML={{ __html: sanitize(brandName) }} />
          </Trans>
        </Heading>
        <StyledCloseButton onClick={onClose} />
      </StyledInfoHeader>
      <Box overflowY="auto" maxHeight="calc(100vh - 70px)">
        {brandInfo.about_popup_image && (
          <img width="100%" src={brandInfo.about_popup_image} alt={brandName} />
        )}
        <StyledBrandInfoContent>
          <div
            dangerouslySetInnerHTML={{
              __html: sanitize(brandInfo.about_brand_text),
            }}
          />
          {brandInfo.good_on_you_info && (
            <>
              <StyledConsciousContent>
                <Heading
                  as="h3"
                  fontSize={20}
                  lineHeight="24px"
                  mb={2}
                  letterSpacing="initial"
                  sx={{ textTransform: "unset" }}
                >
                  <Trans>{brandName} is an ethically rated brand</Trans>
                </Heading>
                <Text as="p">
                  <Trans>
                    {brandName} has been given an overall rating of {ratingInfo}{" "}
                    by Good On You® - an independent sustainability ratings
                    system based on public disclosures. This means that such
                    brands adopt policies and practices to manage people,
                    planet, and/or animal welfare issues across their supply
                    chains, and often demonstrate leadership in one or more of
                    these areas.
                  </Trans>
                </Text>
              </StyledConsciousContent>
              <GoodOnYou
                info={brandInfo.good_on_you_info}
                brandName={brandName}
              />
            </>
          )}
        </StyledBrandInfoContent>
      </Box>
    </StyledBrandInfo>
  );
};

export default BrandInfo;
